import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "dialog", "dropdown", "input", "option", "panel"]
  static values = { placeholder: { type: String, default: "Select..." } }

  #savedScrollY = 0
  #closeTimer = null
  #dialogTimer = null

  connect() {
    if (this.hasInputTarget && this.inputTarget.value) {
      const label = this.#labelForValue(this.inputTarget.value)
      if (label) this.updateButton(label, true)
    } else {
      this.updateButton(this.placeholderValue, false)
    }

    this.boundClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    clearTimeout(this.#closeTimer)
    clearTimeout(this.#dialogTimer)
    document.body.style.overflow = ""
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target) && this.hasDropdownTarget) {
      this.closeDropdown()
    }
  }

  selectOption(event) {
    const option = event.currentTarget
    const value = option.dataset.value
    const label = option.querySelector("span")?.textContent?.trim() || value

    this.#setValue(value)
    this.updateButton(label, true)
    this.#updateAllHighlights(value)
    this.closeWithDelay()
  }

  open(event) {
    event.preventDefault()

    if (this.isDesktop()) {
      this.toggleDropdown()
    } else if (this.hasDialogTarget) {
      this.#savedScrollY = window.scrollY
      this.#openDialog()
    }
  }

  close() {
    if (this.hasDialogTarget && this.dialogTarget.open) {
      this.#closeDialog()
    }
    this.closeDropdown()
  }

  toggleDropdown() {
    if (!this.hasDropdownTarget) return

    const isOpen = this.dropdownTarget.dataset.state === "open"
    if (isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown() {
    if (!this.hasDropdownTarget) return
    this.dropdownTarget.dataset.state = "open"
    this.dropdownTarget.classList.remove("opacity-0", "scale-95", "pointer-events-none")
    this.dropdownTarget.classList.add("opacity-100", "scale-100", "pointer-events-auto")
  }

  closeDropdown() {
    if (!this.hasDropdownTarget) return
    this.dropdownTarget.dataset.state = "closed"
    this.dropdownTarget.classList.add("opacity-0", "scale-95", "pointer-events-none")
    this.dropdownTarget.classList.remove("opacity-100", "scale-100", "pointer-events-auto")
  }

  isDesktop() {
    return window.matchMedia("(min-width: 1025px)").matches
  }

  updateButton(text, isSelected) {
    if (!this.hasButtonTarget) return

    this.buttonTarget.textContent = text
    this.buttonTarget.classList.toggle("text-gray-500", !isSelected)
    this.buttonTarget.classList.toggle("text-gray-900", isSelected)
  }

  closeWithDelay() {
    this.#closeTimer = setTimeout(() => {
      if (this.hasDialogTarget && this.dialogTarget.open) {
        this.#closeDialog()
      }
      this.closeDropdown()
    }, 150)
  }

  #openDialog() {
    this.dialogTarget.showModal()
    document.body.style.overflow = "hidden"

    requestAnimationFrame(() => {
      this.panelTarget.classList.remove("translate-y-full")
    })
  }

  #closeDialog() {
    this.panelTarget.classList.add("translate-y-full")

    this.#dialogTimer = setTimeout(() => {
      this.dialogTarget.close()
      document.body.style.overflow = ""
      window.scrollTo({ top: this.#savedScrollY, behavior: "instant" })
    }, 300)
  }

  #setValue(value) {
    if (!this.hasInputTarget) return
    this.inputTarget.value = value
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  #updateAllHighlights(selectedValue) {
    this.optionTargets.forEach(option => {
      const isSelected = option.dataset.value === selectedValue
      option.classList.toggle("selected", isSelected)
      option.setAttribute("aria-selected", isSelected)
    })
  }

  #labelForValue(value) {
    const match = this.optionTargets.find(el => el.dataset.value === value)
    return match?.querySelector("span")?.textContent?.trim() || null
  }
}
